import { Monitor, Moon, Sun } from 'lucide-react';
import { motion } from 'motion/react';
import { useId } from 'react';
import { useTheme } from '@/hooks/useTheme';
import type { ThemePreference } from '@/types';
import { springSnappy } from '@/animations/variants';
import { cn } from '@/utils/cn';

const OPTIONS: ReadonlyArray<{ value: ThemePreference; icon: typeof Sun; label: string }> = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'system', icon: Monitor, label: 'System' },
  { value: 'dark', icon: Moon, label: 'Dark' },
];

export function ThemeToggle({ className }: { className?: string }) {
  const { preference, setPreference } = useTheme();
  const groupId = useId();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn('inline-flex items-center gap-0.5 rounded-full bg-inset p-1', className)}
    >
      {OPTIONS.map(({ value, icon: Icon, label }) => {
        const isActive = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            onClick={() => setPreference(value)}
            className={cn(
              'relative grid size-7 place-items-center rounded-full transition-colors duration-200',
              isActive ? 'text-ink' : 'text-subtle hover:text-muted',
            )}
          >
            {isActive && (
              <motion.span
                layoutId={`${groupId}-theme-pill`}
                transition={springSnappy}
                className="absolute inset-0 rounded-full bg-raised shadow-xs ring-hairline"
              />
            )}
            <Icon className="relative size-3.5" strokeWidth={2.1} />
          </button>
        );
      })}
    </div>
  );
}
